import React, {useState, useEffect, useRef} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {OrgService, WebhookOrgJobFairInfo} from 'app/services/OrgService'
import {EmptyGuid, JobFairStatus, Roles} from 'app/types'
import {Amplify, Auth} from 'aws-amplify'
import {useAuth} from 'app/context/auth-context'
import {requestPassword} from 'app/modules/auth/redux/AuthCRUD'
import {Registration} from '../auth/components/Registration'
import Confirmation from '../auth/components/Confirmation'
import {VJFPUserService} from 'app/services/VJFPUserService'
import {toAbsoluteUrl} from '_metronic/helpers'
import {Alert, Button} from 'react-bootstrap-v5'
import {AppModal} from '../shared/modals/AppModal'

import {JSRoutes} from './JSPage'
import {UrlParamReplace, getTimezoneOffset} from 'app/helpers/MiscUtils'
import useIsMounted from 'app/hooks/useIsMounted'
import moment from 'moment'
import {JobseekerRegistrationService} from 'app/services/JobseekerRegistrationService'
import {timeService} from 'app/services/TimeService'

Amplify.configure({
  Storage: {
    AWSS3: {
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
      bucket: process.env.REACT_APP_S3_BUCKET || 'videojobfairs-docs-main',
    },
  },
})

const JobFairAuthComponent = () => {
  const {orgId, jobFairId} = useParams<{orgId: string; jobFairId: string}>()
  const history = useHistory()

  const [email, setEmail] = useState('')
  const [userExists, setUserExists] = useState<boolean | null>(null)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [orgDetails, setOrgDetails] = useState<WebhookOrgJobFairInfo>()
  const [userRole, setUserRole] = useState(null)
  const [showForgotPasswordAlert, setShowForgotPasswordAlert] = useState(false)
  const [authMode, setAuthMode] = useState<'select' | 'signin' | 'create'>('select')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [confirmationEmail, setConfirmationEmail] = useState<string>('')
  const [confirmationPassword, setConfirmationPassword] = useState<string>('')
  const {user, login, logout} = useAuth()
  const togglePasswordVisibility = () => setShowPassword(!showPassword)
  const isMounted = useIsMounted('JobfairAuthComponent')
  const roleBlockedRef = useRef(false)

  const getEventStatus = () => {
    if (!orgDetails) return JobFairStatus.Upcoming

    const {startDate, startTime, timezone, durationHours, durationMinutes} = orgDetails.jobFairEvent
    const timeZoneOffset = getTimezoneOffset(timezone)

    const startMoment = moment(
      `${startDate} ${startTime} ${timeZoneOffset}`,
      'YYYY-MM-DD HH:mm ZZ'
    ).utc()

    const endMoment = startMoment
      .clone()
      .add(durationHours, 'hours')
      .add(durationMinutes || 0, 'minutes')

    const extendedEndMoment = endMoment.clone().add(1.5, 'hours')

    const currentDateTime = timeService.getCurrentMoment()

    if (currentDateTime.isBetween(startMoment, endMoment, undefined, '[]')) {
      return JobFairStatus.Started
    } else if (currentDateTime.isBetween(endMoment, extendedEndMoment, undefined, '[]')) {
      return JobFairStatus.ClosingSession
    } else if (currentDateTime.isBefore(startMoment)) {
      return JobFairStatus.Upcoming
    }

    return JobFairStatus.Ended
  }

  const eventStatus = getEventStatus()

  const ensureRegistrationAndRedirect = async (attributes: any) => {
    const {sub} = attributes
    if (!sub) return

    try {
      if (orgDetails && !orgDetails.isActivated) {
        setError('This job fair is not active.')
        return
      }

      const jobseekerRegistrationService = new JobseekerRegistrationService()
      const registration = await jobseekerRegistrationService.getRegistration(orgId, jobFairId, sub)

      if (!registration) {
        await jobseekerRegistrationService.createRegistration(orgId, jobFairId, sub, {
          email: attributes.email,
          given_name: attributes.given_name,
          family_name: attributes.family_name,
          phone_number: attributes.phone_number
        })
      }

      const vjfpUserService = new VJFPUserService()
      const vjfpUser = await vjfpUserService.getByEmail(attributes.email)

      if (vjfpUser?.isProfileComplete !== true) {
        history.push(UrlParamReplace(JSRoutes.Profile))
      } else {
        history.push(UrlParamReplace(JSRoutes.Lobby))
      }
    } catch (error) {
      console.error('Error ensuring registration:', error)
      // Optionally handle error (e.g., show message), but mostly likely we want to let the user try again or contact support
      setError('Failed to complete registration. Please try again.')
    }
  }

  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!orgId || !jobFairId) return
      setError('')
      try {
        const data = await new OrgService().getWebhookOrgJobFairInfo(orgId, jobFairId)
        if (!isMounted()) return
        setOrgDetails(data)
        try {
          sessionStorage.setItem('vjfp.orgDetails', JSON.stringify(data))
        } catch (storageErr) {
          console.warn('Could not persist orgDetails to sessionStorage', storageErr)
        }
      } catch (err) {
        if (!isMounted()) return
        setError('Failed to fetch organization details.')
        try {
          sessionStorage.removeItem('vjfp.orgDetails')
        } catch (storageErr) {
          console.warn('Could not clear orgDetails from sessionStorage', storageErr)
        }
      }
    }

    fetchOrgDetails()
  }, [orgId, jobFairId, isMounted])

  useEffect(() => {
    const checkUser = async () => {
      try {
        if (!user) {
          setUserRole(null)
          if (!roleBlockedRef.current) {
            setError('')
          }
          roleBlockedRef.current = false
          return
        }
        const isApplicantRole = user.roles?.includes(Roles.Applicant)
        if (isApplicantRole) {
          try {
            const userInfo = await Auth.currentUserInfo()
            if (userInfo?.attributes) {
              if (!orgDetails) return
              if (!orgDetails.isActivated) return

              await ensureRegistrationAndRedirect(userInfo.attributes)
              return // Ensure we don't hit the duplicate logout logic if we are redirecting
            }
          } catch (error) {
            console.error('Error checking/creating registration:', error)
          }
          // Fallback redirect if something fails above but we are an applicant
          history.push(UrlParamReplace(JSRoutes.Lobby))
        } else {
          roleBlockedRef.current = true
          setError('You have a recruiter account. You cannot login as a job seeker.')
          await logout(orgId, user.id)
        }
      } catch {
        setUserRole(null)
      }
    }

    checkUser()
  }, [orgId, user, orgDetails])

  const handleCheckUser = async () => {
    if (!email || email.trim() === '') {
      setError('Email is required.')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.')
      return
    }

    setLoading(true)
    setError('')
    try {
      const vjfpUserService = new VJFPUserService()
      const response = await vjfpUserService.getUserExistsWithRole(email)
      if (response?.exists) {
        if (!response.roles?.includes(Roles.Applicant)) {
          history.push('/auth/login?email=' + encodeURIComponent(email))
        }
        setUserExists(true)
      } else {
        setUserExists(false)
      }
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async () => {
    setLoading(true)
    try {
      const vjfpUserService = new VJFPUserService()
      const userCheck = await vjfpUserService.getUserExistsWithRole(email)
      if (userCheck?.exists && !userCheck.roles?.includes(Roles.Applicant)) {
        setError('You have a recruiter account. You cannot login as a job seeker.')
        return
      }

      setError('')

      const response = (await login(email, password)) as any
      if (!response?.attributes?.sub) {
        return
      }

      await ensureRegistrationAndRedirect(response.attributes)
    } catch (err: any) {
      // If the user exists but is not confirmed, prompt for confirmation
      const authErrName = err?.name || err?.code || ''
      if (authErrName === 'UserNotConfirmedException' || authErrName === 'UserNotConfirmed') {
        setConfirmationEmail(email)
        setConfirmationPassword(password)
        setShowConfirmation(true)
        setError('Account not confirmed. Please enter the confirmation code sent to your email.')
        setLoading(false)
        return
      }

      setError(err.message || 'An unexpected error occurred. Please try again.')
    } finally {
      if (isMounted()) {
        setLoading(false)
      }
    }
  }
  return (
    <div
      className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
      style={{
        backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/progress-hd.png')})`,
      }}
    >
      <div className='d-flex flex-column flex-root flex-column-fluid align-items-center justify-content-center min-vh-100'>
        {authMode === 'create' && orgDetails?.isActivated === true ? (
          <>
            <div className='text-center'>
              <a href='#'>
                <img
                  alt='Logo'
                  src={toAbsoluteUrl('/media/logos/logo150.png')}
                  className='h-45px'
                />
              </a>
            </div>
            <Registration
              roles='Applicant'
              signInHandler={() => setAuthMode('signin')}
              orgId={EmptyGuid}
              jobFairId={jobFairId}
              afterLoginHandler={async () => {
                try {
                  const userInfo = await Auth.currentUserInfo()
                  if (userInfo?.attributes) {
                    await ensureRegistrationAndRedirect(userInfo.attributes)
                  } else {
                    history.push(UrlParamReplace(JSRoutes.Profile))
                  }
                } catch (error) {
                  history.push(UrlParamReplace(JSRoutes.Profile))
                }
              }}
            />
          </>
        ) : (
          <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
            <div className='text-center mb-10'>
              <a href='#'>
                <img
                  alt='Logo'
                  src={toAbsoluteUrl('/media/logos/logo150.png')}
                  className='h-45px'
                />
              </a>
            </div>

            {!orgDetails && !error && (
              <div className='d-flex justify-content-center mb-10'>
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                <span className='ms-2'>Loading event details...</span>
              </div>
            )}

            {orgDetails && (
              <div className='text-center mb-10'>
                <h1 className='text-dark mb-3'>Welcome to {orgDetails.orgName}'s Job Fair!</h1>
                {!orgDetails.isActivated && (
                  <Alert variant='danger' className='mb-10 text-start'>
                    <h4 className='alert-heading'>Job Fair Inactive</h4>
                    <p>
                      This job fair is currently not active. Registration and sign-in are disabled
                      at this time. Please contact the event organizer for more information.
                    </p>
                  </Alert>
                )}
                {eventStatus === JobFairStatus.Ended && (
                  <Alert variant='warning' className='mb-10 text-start'>
                    <h4 className='alert-heading'>Past Event</h4>
                    <p>
                      This job fair has concluded. You can still sign in, but the live event is no
                      longer active.
                    </p>
                  </Alert>
                )}
                {eventStatus === JobFairStatus.ClosingSession && (
                  <Alert variant='info' className='mb-10 text-start'>
                    <h4 className='alert-heading'>Event Wrapping Up</h4>
                    <p>
                      This job fair is currently in its closing session. Some features may be
                      limited as the event concludes.
                    </p>
                  </Alert>
                )}
                <Alert variant='info'>
                  <p className='mb-5'>
                    Event Date: {moment(orgDetails.jobFairEvent.startDate).format('MMMM D, YYYY')}
                  </p>
                  <p className='mb-5'>
                    Time: {moment(orgDetails.jobFairEvent.startTime, 'HH:mm:ss').format('h:mm A')} (
                    {orgDetails.jobFairEvent.timezone})
                  </p>
                  <p>Duration: {orgDetails.jobFairEvent.durationHours} hours</p>
                </Alert>
                {orgDetails.sponsor &&
                  (orgDetails.sponsor.sponsoredByText || orgDetails.sponsor.name) && (
                    <div className='text-gray-400 fw-bold fs-4'>
                      {orgDetails.sponsor.sponsoredByText ||
                        `Sponsored by: ${orgDetails.sponsor.name}`}
                    </div>
                  )}
              </div>
            )}

            {error && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{error}</div>
              </div>
            )}

            {orgDetails?.isActivated === true && authMode === 'select' && (
              <div className='d-flex flex-column gap-5'>
                <button
                  className='btn btn-lg btn-primary w-100'
                  onClick={() => setAuthMode('signin')}
                >
                  Sign In
                </button>
                <div className='separator separator-content my-5'>
                  <span className='text-muted'>OR</span>
                </div>
                <button
                  className='btn btn-lg btn-light-primary w-100'
                  onClick={() => setAuthMode('create')}
                >
                  Create an Account
                </button>
              </div>
            )}

            {orgDetails?.isActivated === true && authMode === 'signin' && (
              <div className='fv-row mb-10'>
                <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                <input
                  type='email'
                  className='form-control form-control-lg form-control-solid'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='Email'
                  required
                />
                <label className='form-label fs-6 fw-bolder text-dark mt-5'>Password</label>
                <div className='input-group'>
                  <input
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    placeholder='Enter your password'
                    autoComplete='current-password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className='form-control form-control-lg form-control-solid'
                    disabled={loading}
                    maxLength={128}
                  />
                  <button
                    title={showPassword ? 'Hide password' : 'Show password'}
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={togglePasswordVisibility}
                    disabled={loading}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i
                      className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}
                      aria-hidden='true'
                    ></i>
                  </button>
                </div>
                <div className='d-flex justify-content-between mt-5'>
                  <Button variant='link' onClick={() => setAuthMode('select')}>
                    Back
                  </Button>
                  <Button
                    variant='link'
                    onClick={() => setShowForgotPasswordAlert(true)}
                    disabled={loading}
                  >
                    Forgot Password?
                  </Button>
                </div>
                <button
                  className='btn btn-lg btn-primary w-100 mt-5'
                  onClick={handleLogin}
                  disabled={loading}
                >
                  {!loading ? (
                    'Sign In'
                  ) : (
                    <span className='indicator-progress'>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            )}

            <AppModal
              show={showForgotPasswordAlert}
              successText='Send verification code'
              onHide={() => setShowForgotPasswordAlert(false)}
              onSuccess={async () => {
                setLoading(true)
                const user = await new VJFPUserService().getByEmail(email)
                if (!user?.username) {
                  setError('User not found.')
                  return
                }
                await requestPassword(user.username)
                setLoading(false)
                setShowForgotPasswordAlert(false)
                history.push(
                  `/auth/forgot-password-code-verify/${encodeURIComponent(
                    email
                  )}?orgId=${orgId}&jobfairId=${jobFairId}`
                )
              }}
              title='Forgot password?'
            >
              This action will send a password reset code to your email. Once received, kindly check
              your email and enter the verification code in next screen.
            </AppModal>

            {/* Confirmation modal shown when user tries to sign in but is not confirmed */}
            <Confirmation
              asModal
              show={showConfirmation}
              autoResend={true}
              email={confirmationEmail}
              password={confirmationPassword}
              onConfirmed={async () => {
                // After successful confirmation, attempt login and continue the normal flow
                setShowConfirmation(false)
                setLoading(true)
                setError('')
                try {
                  const response = (await login(confirmationEmail, confirmationPassword)) as any
                  if (!response?.attributes?.sub) {
                    setLoading(false)
                    return
                  }
                  await ensureRegistrationAndRedirect(response.attributes)
                } catch (err: any) {
                  setError(err?.message || 'Sign in after confirmation failed.')
                } finally {
                  setLoading(false)
                }
              }}
              onCancel={() => setShowConfirmation(false)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default JobFairAuthComponent
